<script setup lang="ts">
interface Props {
  items: Record<string, unknown>[] | boolean;
  lastScrolled?: Record<string, unknown> | boolean;
  scroll?: boolean;
  selected?: string | boolean;
  // The parent page's filter-strip element, for the scroll-offset math in
  // scrollPageToSection() — only reached when `scroll` is true. Pass the
  // page's own filtersRef so the offset works regardless of that element's
  // class name — store/[slug].vue, premium/store/[slug].vue,
  // premium/menu/[slug].vue, and orders/[id]/edit.vue each namespace their
  // filter-strip class differently, and all four pass it. Callers that set
  // `scroll: false` (e.g. market/index.vue) never call scrollPageToSection
  // and can omit filterEl entirely.
  filterEl?: HTMLElement | null;
}

const props = withDefaults(defineProps<Props>(), {
  lastScrolled: false,
  scroll: true,
  selected: false,
});

const emit = defineEmits<{
  returnSelection: [slug: string];
}>();

const scrollContainer = ref<HTMLElement | null>(null);
const selectedItem = ref<string | false>(false);
/** Track whether a programmatic scroll is in progress so the scroll
 *  handler doesn't immediately overwrite the visual highlight. */
const isScrolling = ref(false);
let scrollTimer: ReturnType<typeof setTimeout> | null = null;

watch(
  () => props.lastScrolled,
  (to) => {
    if (isScrolling.value) return; // ignore scroll-handler updates during programmatic scroll
    if (to && typeof to === "object") {
      const target = (to as Record<string, unknown>).slug || (to as Record<string, unknown>).id;
      if (target) scrollToSlug(String(target));
    }
  },
);

watch(
  () => props.selected,
  (to) => {
    if (to && typeof to === "string") {
      selectedItem.value = to;
      scrollToSlug(to);
    }
  },
);

onMounted(() => {
  if (props.selected && typeof props.selected === "string") {
    selectedItem.value = props.selected;
  }
  if (props.lastScrolled && typeof props.lastScrolled === "object") {
    nextTick(() => {
      const slug = (props.lastScrolled as Record<string, unknown>).slug;
      if (slug) scrollToSlug(String(slug));
    });
  }
});

function isSelected(slug: string): boolean {
  // While a programmatic scroll is in progress, use the clicked item
  if (isScrolling.value && selectedItem.value) {
    return selectedItem.value === slug;
  }
  if (props.scroll && props.lastScrolled && typeof props.lastScrolled === "object") {
    return (props.lastScrolled as Record<string, unknown>).id === slug;
  }
  return selectedItem.value === slug;
}

function updateSelectedItem(slug: string) {
  selectedItem.value = slug;
  emit("returnSelection", slug);
  scrollToSlug(slug);
  if (props.scroll) scrollPageToSection(slug);
}

function scrollToSlug(slug: string) {
  if (!scrollContainer.value) return;
  const items = Array.isArray(props.items) ? props.items : [];
  const idx = items.findIndex(
    (it: Record<string, unknown>) => (it.slug || it.id) === slug,
  );
  if (idx === -1) return;
  const children = scrollContainer.value.children;
  if (children[idx]) {
    const el = children[idx] as HTMLElement;
    const container = scrollContainer.value;
    const targetLeft = el.offsetLeft - container.offsetWidth / 2 + el.offsetWidth / 2;
    container.scrollTo({ left: targetLeft, behavior: "smooth" });
  }
}

function scrollPageToSection(slug: string) {
  // The slug from categoryItems is already normalised — use it directly.
  const target = document.getElementById(slug);
  if (!target) return;

  // Calculate offset dynamically based on fixed navbar + filter heights
  const nav = document.querySelector(".navbar") as HTMLElement | null;
  const navHeight = nav?.offsetHeight ?? 80;
  const filterHeight = props.filterEl?.offsetHeight ?? 80;
  const offset = -(navHeight + filterHeight + 16);

  const top = target.getBoundingClientRect().top + window.scrollY + offset;

  // Flag that a programmatic scroll is in progress
  isScrolling.value = true;
  if (scrollTimer) clearTimeout(scrollTimer);
  scrollTimer = setTimeout(() => {
    isScrolling.value = false;
  }, 800);

  window.scrollTo({ top, behavior: "smooth" });
}

function scrollLeft() {
  scrollContainer.value?.scrollBy({ left: -200, behavior: "smooth" });
}

function scrollRight() {
  scrollContainer.value?.scrollBy({ left: 200, behavior: "smooth" });
}

// --- Drag-to-scroll ---
const DRAG_THRESHOLD = 5;
let isDragging = false;
let startX = 0;
let scrollStart = 0;

function onPointerDown(e: PointerEvent) {
  if (!scrollContainer.value) return;
  isDragging = false;
  startX = e.clientX;
  scrollStart = scrollContainer.value.scrollLeft;
  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerUp, { once: true });
}

function onPointerMove(e: PointerEvent) {
  if (!scrollContainer.value) return;
  const dx = e.clientX - startX;
  if (!isDragging && Math.abs(dx) >= DRAG_THRESHOLD) {
    isDragging = true;
  }
  if (isDragging) {
    e.preventDefault();
    scrollContainer.value.scrollLeft = scrollStart - dx;
  }
}

function onPointerUp() {
  document.removeEventListener("pointermove", onPointerMove);
  if (isDragging) {
    // Swallow the click that follows a drag so it doesn't select a filter item.
    // Auto-remove after 200ms in case no click fires (pointer moved too far),
    // so it doesn't eat the user's next intentional click.
    const handler = (ev: Event) => {
      ev.stopPropagation();
      ev.preventDefault();
    };
    requestAnimationFrame(() => {
      scrollContainer.value?.addEventListener("click", handler, { capture: true, once: true });
      setTimeout(() => {
        scrollContainer.value?.removeEventListener("click", handler, { capture: true });
      }, 200);
    });
  }
  isDragging = false;
}
</script>

<template>
  <div class="list-filter">
    <div class="list-filter__nav">
      <button
        class="list-filter__nav--prev"
        @click="scrollLeft"
      >
        <fa :icon="['fal', 'chevron-left']" />
      </button>
      <div
        ref="scrollContainer"
        class="list-filter__wrapper"
        @pointerdown="onPointerDown"
      >
        <div
          v-for="(item, i) in (Array.isArray(items) ? items : [])"
          :key="`item_${i}`"
          class="list-filter__wrapper--slide"
        >
          <p
            :class="isSelected(String((item as Record<string, unknown>).slug)) && 'selected'"
            @click="updateSelectedItem(String((item as Record<string, unknown>).slug))"
          >
            {{ (item as Record<string, unknown>).name }}
          </p>
        </div>
        <div
          v-if="$slots.trailing"
          class="list-filter__wrapper--trailing"
        >
          <slot name="trailing" />
        </div>
      </div>
      <button
        class="list-filter__nav--next"
        @click="scrollRight"
      >
        <fa :icon="['fal', 'chevron-right']" />
      </button>
    </div>
  </div>
</template>

<style lang="scss">
$laptop: 1200px;
$tablet: 768px;
$mobile: 600px;

.list-filter {
  cursor: pointer;
  width: 100%;
  &__nav {
    display: flex;
    align-items: center;
    position: relative;
    &--prev,
    &--next {
      background: none;
      border: none;
      cursor: pointer;
      width: 2rem;
      height: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: var(--color-grey-2);
      border-radius: 50%;
      transition: all 0.2s ease;
      &:hover {
        background: var(--color-grey-3);
        color: var(--color-grey-1);
      }
      @media (max-width: $mobile) {
        display: none;
      }
    }
    &--prev {
      margin-right: 0.25rem;
    }
    &--next {
      margin-left: 0.25rem;
    }
  }
  &__wrapper {
    display: flex;
    overflow-x: auto;
    scroll-behavior: smooth;
    cursor: grab;
    &:active {
      cursor: grabbing;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    height: 5rem;
    align-items: center;
    width: 100%;
    // Mobile: drop the wrapper height so the strip doesn't dominate the
    // viewport. Use a small gap between pills instead of large vertical
    // margins so the row reads as a tidy single track of chips.
    @media (max-width: $tablet) {
      height: auto;
      padding: 0.25rem 0;
      gap: 0.4rem;
    }
    &--slide {
      cursor: pointer;
      flex-shrink: 0;
      text-align: center;
      display: block;
      // Inactive category pill: white background with a thin neutral
      // outline. Selected state uses a pink-4 wash (see .selected below).
      p {
        user-select: none;
        white-space: nowrap;
        margin: 1rem 0.2rem;
        color: var(--color-grey-1) !important;
        border: 0.0625rem solid var(--color-grey-3);
        background: var(--color-white-1);
        cursor: pointer;
        padding: 0.7rem 1.2rem;
        border-radius: var(--radius);
        font-family: "GalanoGrotesqueMedium", sans-serif;
        font-size: 0.9rem;
        font-weight: var(--weight-medium);
        transition:
				background-color 0.2s ease,
				color 0.2s ease,
				border-color 0.2s ease,
				box-shadow 0.2s ease;
        &:hover {
          background: var(--color-grey-3);
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        }
        @media (max-width: $tablet) {
          // Mobile: pills are spaced by the wrapper's gap, so kill the
          // 1rem-vertical / 0.2rem-horizontal pill margin which was
          // doubling up against the gap and making the strip look
          // staggered. Slightly slimmer padding for more pills per view.
          margin: 0;
          padding: 0.55rem 1rem;
          font-size: 0.85rem;
        }
      }
      &:first-child p {
        margin-left: 0;
      }
    }
    &--trailing {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      margin-left: 0.2rem;
    }
    // Selected category pill: soft pink wash with pink ink. Quieter than
    // a full pink fill — keeps strong pink for primary CTAs only.
    .selected {
      color: var(--color-pink-1) !important;
      background-color: var(--color-pink-4) !important;
      border-color: var(--color-pink-4) !important;
      box-shadow: none;
      &:hover {
        background-color: var(--color-pink-4) !important;
        color: var(--color-pink-1) !important;
        border-color: var(--color-pink-4) !important;
      }
    }
  }
  .greyed {
    opacity: 0.2;
  }
}
</style>
